import React from 'react';
import styled, { useTheme } from 'styled-components';

import { spacing } from 'ui/theme/utils';

export const ToggleSwitch = ({ size, ...rest }) => {
  const theme = useTheme();
  const sizeProps =
    size === 'small'
      ? {
          width: 36,
          height: 20,
          translate: 16,
          sliderWidth: 14,
          sliderHeight: 14,
          sliderLeft: 3,
        }
      : {
          width: 48,
          height: 26,
          translate: 22,
          sliderWidth: 20,
          sliderHeight: 20,
          sliderLeft: 3,
        };

  return (
    <Toggle
      {...rest}
      {...sizeProps}
      backgroundColorChecked={theme.colors.primary}
      backgroundColorUnchecked={theme.colors.lightGrey}
      backgroundColorButton={theme.colors.white}
    />
  );
};

// Based on: https://github.com/guillaumemorin/react-styled-toggle
const Toggle = ({
  onChange,
  checked,
  disabled,
  width = 60,
  height = 34,
  translate = 26,
  backgroundColorChecked = '#2193F3',
  backgroundColorUnchecked = '#ccc',
  backgroundColorButton = '#fff',
  name,
  value,
  labelRight,
  labelLeft,
  sliderWidth = 26,
  sliderHeight = 26,
  sliderLeft = 4,
  testId,
  isLabelColored = false,
  ...rest
}) => {
  return (
    <GlobalContainer data-testid={testId} {...rest}>
      {labelLeft && <Label>{labelLeft}</Label>}
      <InputContainer width={width} height={height}>
        <Input
          type="checkbox"
          name={name}
          value={value}
          checked={checked || false}
          onChange={onChange}
          disabled={disabled}
          translate={translate}
          backgroundColorUnchecked={backgroundColorUnchecked}
          backgroundColorChecked={backgroundColorChecked}
        />
        <Slider
          sliderWidth={sliderWidth}
          sliderHeight={sliderHeight}
          sliderLeft={sliderLeft}
          backgroundColorUnchecked={backgroundColorUnchecked}
          backgroundColorButton={backgroundColorButton}
        />
      </InputContainer>
      {labelRight && <Label isLabelColored={isLabelColored && checked}>{labelRight}</Label>}
    </GlobalContainer>
  );
};

const GlobalContainer = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) => spacing(props)}
`;

const InputContainer = styled.label`
  position: relative;
  display: inline-block;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  > input {
    display: none;
  }
`;

const Input = styled.input`
  &:checked + span {
    background-color: ${({ backgroundColorChecked }) => backgroundColorChecked};
  }
  &:disabled + span {
    background-color: ${({ backgroundColorUnchecked }) => backgroundColorUnchecked};
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:disabled:checked + span {
    background-color: ${({ backgroundColorChecked }) => backgroundColorChecked};
    opacity: 0.4;
    cursor: not-allowed;
  }
  &:focus + span {
    box-shadow: 0 0 1px #2196f3;
  }
  &:checked + span:before {
    -webkit-transform: translateX(${({ translate }) => translate}px);
    -ms-transform: translateX(${({ translate }) => translate}px);
    transform: translateX(${({ translate }) => translate}px);
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  display: flex;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ backgroundColorUnchecked }) => backgroundColorUnchecked};
  -webkit-transition: 200ms;
  transition: 200ms;
  border-radius: 34px;
  &:before {
    position: relative;
    border-radius: 50%;
    content: '';
    height: ${({ sliderHeight }) => sliderHeight}px;
    width: ${({ sliderWidth }) => sliderWidth}px;
    left: ${({ sliderLeft }) => sliderLeft}px;
    background-color: ${({ backgroundColorButton }) => backgroundColorButton};
    -webkit-transition: 200ms;
    transition: 200ms;
  }
`;

const Label = styled.span`
  color: #777;
  font-size: 15px;
  font-family: helvetica;
  padding: 0 10px;
  ${({ isLabelColored, theme }) =>
    isLabelColored &&
    `
    color: ${theme.colors.primary};
  `}
`;
